import { IFilterViewModel, FilterComponent, FilterJson, Dictionary, FilterTerm, Connector, SortComponent, CustomFilterChip } from '#/DefaultViewExtensionInterfaces';
import ComponentManager from '@/Management/ComponentManager';
import ChipsViewModel from '@/ViewModels/Controls/ChipsViewModel';
import Messenger from '@/Utility/Messenger';
import FilterChipsChangedMessage from '@/Components/Filter/FilterChipsChangedMessage';
import FilterChipsHelper from '@/Components/Filter/FilterChipsHelper';
import RADApplication from '@/Management/RADApplication';
import DeleteFilterMessageBoxViewModel from '@/ViewModels/Controls/Filter/DeleteFilterMessageBoxViewModel';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';

import User from '@/Model/User';
import ResourceManager from '@/Management/ResourceManager';
import type FilterElementViewModelT from '@/ViewModels/Controls/FilterElementViewModel';
import type FilterSortElementViewModelT from '@/ViewModels/Controls/FilterSortElementViewModel';
import ReplaceFilterMessageBoxViewModel from '@/ViewModels/Controls/Filter/ReplaceFilterMessageBoxViewModel';
import Utilities from '@/Utility/Utilities';
import CurrencyHelper from '@/Utility/CurrencyHelper';
import TypeConverter from '@/Utility/TypeConverter';
import * as DateHelper from '@/Utility/DateHelper'
import SavePublicFilterMessageBoxViewModel from '@/ViewModels/Controls/Filter/SavePublicFilterMessageBoxViewModel';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import * as LoggingManager from '@/Utility/LoggingManager';

const PREFILTER = 'PREFILTER';

/**
 * Class for a LVs ChipsViewModel
 *  with the ability
 *  of saving and restoring saved filterExpressions as named Chips.
 */
export default class FilterChipsViewModel extends ChipsViewModel<string> {

    private filterViewModel: IFilterViewModel;

    private listViewPdoId: string;

    public editPublicChips: KnockoutObservable<boolean>;
    public editPrivateChips: KnockoutObservable<boolean>;

    private readonly currentUser: User;

    private readonly pdTypeName: string;

    private readonly preFilterTooltip: string;
    private preFilterClicked: boolean;
    private customFilterClicked: boolean;
    private operatorResourceLookup = {};
    private sortedByChip = false;
    private sortedByChipJson = {};
    private isSaving: boolean = false;
    public publicUserData: KnockoutObservable<Dictionary<FilterJson>>;
    private waitForPublicData: JQueryDeferred<any>;

    private customFilterData: Array<CustomFilterChip>;

    public ignoreChipsChange: boolean;
    private preventPublicUserDataServerUpdate: boolean;

    public constructor(protected params: any) {
        super(params);

        this.currentUser = RADApplication.getInstance().user;

        if (Utilities.isBlank(params.pdTypeName)) throw new Error('FilterChipsViewModel was instantiated without a pdTypeName');
        if (!params.filter) throw new Error('FilterChipsViewModel was instantiated without a FilterViewModel');

        this.initOperatorResourceLookUp();

        this.pdTypeName = params.pdTypeName;

        this.preFilterTooltip = params.filter.preFilterTooltip;

        this.registerForChipsChangedMessage();
        this.listViewPdoId = this.paramOrDefault(params.listViewPdoId, '-1');
        this.filterViewModel = this.paramOrDefault(params.filter, null);
        this.editPublicChips = this.observableOrDefault(params.editPublicChips, false);
        this.editPrivateChips = this.observableOrDefault(params.editPrivateChips, false);

        this.onClick = this.updateClickHandler(this.onClick);
        this.inputVisible(false);
        this.addNewValue = this.updateAddNewValue(this.addNewValue);

        this.ignoreChipsChange = false;
        this.preventPublicUserDataServerUpdate = false;

        this.customFilterData = [];
        
        // Bei Initialisierung erst Werte füllen, dann subscriptions aufbauen, um Seiteneffekte zu minimieren
        this.isBusy(true);
        this.initPublicUserData().then(() => {
            if (this.isDisposed)
                return;

            this.initChips();
            this.updateTooltips();
            this.registerSubscriptions();
            this.isBusy(false);
        });
    }

    private initOperatorResourceLookUp(): void {
        const resourceManager = ResourceManager.getInstance();
        this.operatorResourceLookup['EQUALS'] = resourceManager.get('EQUALS');
        this.operatorResourceLookup['NOT_EQUALS'] = resourceManager.get('NOT_EQUALS');
        this.operatorResourceLookup['GREATER'] = resourceManager.get('GREATER');
        this.operatorResourceLookup['GREATER_EQUALS'] = resourceManager.get('GREATER_EQUALS');
        this.operatorResourceLookup['LESS'] = resourceManager.get('LESS');
        this.operatorResourceLookup['LESS_EQUALS'] = resourceManager.get('LESS_EQUALS');
        this.operatorResourceLookup['LIKE'] = resourceManager.get('LIKE');
        this.operatorResourceLookup['NOT_LIKE'] = resourceManager.get('NOT_LIKE');
    }

    private initPublicUserData(): JQuery.Promise<void>{
        // First try if public Data has already been loaded
        if (!Utilities.isNullOrUndefined(this.params.publicUserData)) {
            try {
                const dataValue = JSON.parse(this.params.publicUserData);
                this.publicUserData = ko.observable(dataValue);
                this.publicUserData.extend({ notify: 'always' });
                return $.Deferred().resolve();
            } catch (e) {
                //logging
                LoggingManager.logError(LoggingManager.LogLevel.Warn, e, document.location.href + ' init public FilterChips of: ' + this.pdTypeName);
                //if there is no valid public Data for the given List, get the Value from the Server
            }
        }

        // Get and parse public Data from Server
        return RADEnvironmentProxy.getInstance().getPublicUserData({ key: this.buildKey() }).then((dataResult) => {
            if (dataResult != null) {
                try {
                    const dataValue = JSON.parse(dataResult);
                    this.publicUserData = ko.observable(dataValue);
                } catch (e) {
                    //if there is no valid public Data for the given List leave Dictionary empty
                    this.publicUserData = ko.observable({});
                }
            }
            else {
                this.publicUserData = ko.observable({});
            }
            this.publicUserData.extend({ notify: 'always' });
        });
    }

    private initChips(): void {
        if (!this.filterViewModel.shouldShowChips())
            return;

        this.loadPersistedFilterExpressions();
        let chips = this.params.serializedFilter?.activeChips ?? [];
        chips = chips.concat(this.loadPreFilterExpression(chips));
        chips = chips.concat(this.loadCustomFilterChips(true));

        this.setActiveChips(chips, this.values());

        this.addPreFilterTooltip();
    }
    
    private registerSubscriptions(): void {
        this.publicUserData.subscribe((value) => {
            const values = this.originValuesAsArray().filter(chip => !FilterChipsHelper.isPublic(chip));
            const updatedValues = values.concat(this.loadPublicPersistedFilterExpressions());
            this.values(updatedValues);

            if (this.preventPublicUserDataServerUpdate)
                return;

            let json: string;
            if (value === null) {
                json = null;
            }
            else {
                json = window['JSON'].stringify(value);
            }
            // Das Ergebniss des ServerCalls wird verwendet, um zu bestimmen, wann eine Änderung im deleteChipYesCallback an verbundene ALVs kommuniziert werden kann
            this.waitForPublicData = RADEnvironmentProxy.getInstance().updatePublicUserData({
                key: this.buildKey(),
                value: json
            });
            if (value === null) {
                delete this.publicUserData;
            }
        });

        this.defineSubscription(this.values, () => this.updateTooltips());

        if (this.filterViewModel.shouldShowChips()) {
            this.defineSubscription(this.filterViewModel.columns, () => {
                this.loadPersistedFilterExpressions();
            });

            this.defineSubscription(this.activeChips, activeChips => this.assignFilterFromActiveChips(activeChips));
        }
    }

    private registerForChipsChangedMessage(): void {
        Messenger.registerForMessageId('FilterChipsChanged', this.applyMessage.bind(this), this);
    }

    private reloadPublicUserData(): void {
        this.updatePublicUserData(true);
    }

    private applyMessage(message: FilterChipsChangedMessage): void {
        // Wenn es nicht um die selbe Liste geht
        // oder die Message aus dem selben Control kommt, dann kann diese ignoriert werden
        // Es werden nur Messages aus anderen Filtern akzeptiert (z.B. aus Popups)
        if (this.listViewPdoId != message.listViewPdoId || message.chipsControlId == this.identifier)
            return;

        if (message.isPublicChip) {
            this.reloadPublicUserData();
        }
        else {
            if (message.action == 'REMOVE') {
                let values = this.readAllValues(this.originValues);
                if (values.indexOf(message.chipName) > -1) {
                    this.onRemove(message.chipName, false, false);
                }
            }
            else if (message.action == 'ADD') {
                let values = this.readAllValues(this.originValues);
                if (values.indexOf(message.chipName) < 0) {
                    this.onAdd(message.chipName, false);
                }
                else {
                    this.updateTooltips();
                }
            }
        }
    }

    private updateClickHandler(clickHandler: Function): Function {
        return (name: string) => {
            if (name === FilterChipsViewModel.getPreFilterLabel()) {
                this.preFilterClicked = true;
            }

            if (FilterChipsHelper.isCustom(name)) {
                this.customFilterClicked = true;
            }

            clickHandler(name);
        };
    }

    private updateAddNewValue(addNewValue: Function): Function {
        return () => {
            const isFilterEmpty = this.filterViewModel.elements().length <= 1;
            const isSortElementsEmpty = this.filterViewModel.sortElements().length <= 1;
            const isDataEmpty = isFilterEmpty && isSortElementsEmpty;

            const isBlankChipsValue = Utilities.isBlank(this.value());
            const isPreFilterValue = FilterChipsViewModel.getPreFilterLabel() === this.value();
            const isPreFilterActive = _.contains(this.activeChips(), FilterChipsViewModel.getPreFilterLabel());
            const isCustomFilterActive = this.activeChips().some(chip => FilterChipsHelper.isCustom(chip))

            const isNotComplete = this.filterViewModel.elements().some(element => !!element.column() && !element.isComplete());

            const filterHasPDOID = this.filterViewModel.elements().some(x => x.column() === 'PDO_ID');
            const sortHasPDOID = this.filterViewModel.sortElements().some(x => x.sortColumn() === 'PDO_ID');
            const hasPDOID = filterHasPDOID || sortHasPDOID;

            const isInvalid = [isPreFilterValue, isDataEmpty, isBlankChipsValue, isPreFilterActive, isNotComplete, hasPDOID, isCustomFilterActive].some(any => any);

            if (isInvalid) {
                let message = '';
                if (isDataEmpty) {
                    message = 'NO_SAVE_ON_FILTER_EMPTY';
                }
                else if (isBlankChipsValue) {
                    message = 'NO_SAVE_ON_BLANK_NAME';
                }
                else if (isCustomFilterActive) {
                    message = 'NO_SAVE_WITH_ACTIVE_CUSTOM_FILTER';
                }
                else if (isPreFilterValue) {
                    message = 'NO_SAVE_ON_OVERRIDE_PREFILTER';
                }
                else if (isPreFilterActive) {
                    message = 'NO_SAVE_WITH_ACTIVE_PREFILTER';
                }
                else if (isNotComplete) {
                    message = 'NO_SAVE_WITH_INCOMPLETE_ELEMENT';
                }
                else if (hasPDOID) {
                    message = 'NO_SAVE_ON_PDOID_FILTER'
                }

                RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createNoticeMessageBox('', message));

                this.value('');
                return;
            }

            const done = this.saveFilterExpression(this.value().trim());

            $.when(done).then(() =>
                addNewValue()
            );
        };
    }

    private isValidPrefilter(): boolean {
        return Utilities.isNotBlank(this.filterViewModel.preFilter);
    }

    private loadPreFilterExpression(chips: Array<string>): Array<string> {
        if (!this.isValidPrefilter()) {
            return [];
        }

        const label = FilterChipsViewModel.getPreFilterLabel();

        const chipsContainsPrefilter = _.contains(chips,label);

        if (chipsContainsPrefilter) {
            return [];
        }

        return [label];
    }

    private addPreFilterTooltip(): void {
        if (!this.isValidPrefilter()) {
            return;
        }

        this.tooltips()[FilterChipsViewModel.getPreFilterLabel()] = this.preFilterTooltip;
    }

    private assignFilterFromActiveChips(activeChips: Array<string>): void {
        if (this.ignoreChipsChange || this.isSaving) {
            return;
        }

        const dontResetElements = (this.preFilterClicked || this.customFilterClicked)
            && (activeChips.length === 0 || (activeChips.length === 1 && (_.contains(activeChips, FilterChipsViewModel.getPreFilterLabel()) || activeChips.some((chipTitle) => FilterChipsHelper.isCustom(chipTitle)))));
        this.preFilterClicked = false;
        this.customFilterClicked = false;

        // Wenn der Vorfilter (de-)aktiviert wurde und nur noch dieser ODER kein anderer Chip aktiv ist
        if (!dontResetElements) {
            this.filterViewModel.elements([]);
        }

        // if not yet sorted by an chip:
        //  we can clear any manually applied sorting
        //  we must refresh the grid later on
        if (!this.sortedByChip) {
            this.filterViewModel.resetSortElements(true);
        }

        // apply filter-data for active chips
        if (_.isArray(activeChips) && activeChips.length > 0) {
            const filteredActiveChips =
                activeChips.filter(chipTitle => chipTitle !== FilterChipsViewModel.getPreFilterLabel() && !FilterChipsHelper.isCustom(chipTitle));
            const chipsData = filteredActiveChips.map(chipTitle => this.loadNamedFilter(chipTitle)).filter(c => !Utilities.isNullOrUndefined(c));
            const lastMaxExtentCount = _.max(chipsData.map(chipData => chipData.lastExtentCount));

            if (isFinite(lastMaxExtentCount))
                this.filterViewModel.lastMaxExtentCount(lastMaxExtentCount);

            // Nachträglich Prüfung, ob die aktuelle Sortierung noch als Chip vorhanden ist,
            // wenn nicht, wird sie entfernt, ABER nur, wenn es nicht der letzte Chip ist
            if (activeChips.length > 0 && !chipsData.some(chipData => this.sortedByChipJson === chipData)) {
                this.sortedByChip = false;
                this.sortedByChipJson = {};
                this.filterViewModel.resetSortElements(true);
            }
            
            this.filterViewModel.isFilterLoadingDeactivated(true);
            chipsData.forEach(chipData => this.applyChipFilterData(chipData));
            this.filterViewModel.isFilterLoadingDeactivated(false);
        } else {
            // Wenn kein Chip aktiv ist, soll trotzdem die höchste Ergebnismenge genommen werden
            // um eventuell eine Limitüberschreitung zu erkennen
            const allChips = this.values();
            const filteredAllChips =
                allChips.filter(chipTitle => chipTitle !== FilterChipsViewModel.getPreFilterLabel());
            const chipsData = filteredAllChips.map(chipTitle => this.loadNamedFilter(chipTitle));
            const lastMaxExtentCount = _.max(chipsData.map(chipData => chipData?.lastExtentCount ?? 0));
            if (isFinite(lastMaxExtentCount))
                this.filterViewModel.lastMaxExtentCount(lastMaxExtentCount);
        }

        this.filterViewModel.rearrange();

        // reset sortedByChip-state
        if (_.isEmpty(activeChips)) {
            this.sortedByChip = false;
            this.sortedByChipJson = {};
        }

        this.filterViewModel.refreshSorting();
    }

    private applyChipFilterData(filter: FilterJson): void {
        if (Utilities.isNullOrUndefined(filter))
            return;

        if (!Utilities.isNullOrUndefined(filter.components)) {
            const lastComponent = _.last(filter.components);
            filter.components
                .filter(component => !_.isArray(component.filter))
                .filter(component => this.isCompleteFilterComponent(component.filter as FilterTerm))
                .forEach(component => this.addFilterElementForFilterTerm(component.filter as FilterTerm,
                    (component !== lastComponent ? component.connector : 'AND')));
        }

        // if not sorted by any chip, we can change the sort-elements
        if (!this.sortedByChip && !Utilities.isNullOrUndefined(filter.sorting)) {
            for (let i = 0; i < filter.sorting.length; i++) {
                const sorting = filter.sorting[i];
                if (this.isCompleteSortComponent(sorting)) {
                    this.addSortRow(sorting.sortColumn, sorting.sortDirection, i);
                }
            }

            this.sortedByChip = true;
            this.sortedByChipJson = filter;
        }
    }

    private isCompleteFilterComponent(term: FilterTerm): boolean {
        return Utilities.isNotBlank(term.column) && Utilities.isNotBlank(term.operator) && !!term.value;
    }

    private isCompleteSortComponent(sorting: SortComponent): boolean {
        return Utilities.isNotBlank(sorting.sortColumn) && Utilities.isNotBlank(sorting.sortDirection);
    }

    public saveFilterExpression(filterName: string): JQueryPromise<void> {
        const dfd = $.Deferred();
        const promise = dfd.promise();

        const isFilterEmpty = _.isEmpty(this.filterViewModel.elements());
        if (isFilterEmpty) {
            console.warn('saveFilterExpression() was called with empty filter elements');
            dfd.reject();
            return promise;
        }

        const name = this.editPublicChips() ? FilterChipsHelper.addPublicPrefix(filterName) : FilterChipsHelper.addPrivatePrefix(filterName);
        const existsFilter = this.editPublicChips() ? this.existsPublicFilterData(filterName) : this.existsPrivateFilterData(filterName);
        if (existsFilter) {
            const yesCallback = this.saveFilterYesCallback.bind(this, name, dfd);
            const messageBox = new ReplaceFilterMessageBoxViewModel(yesCallback);
            RADApplication.getInstance().showMessageBox(messageBox);

            return promise;
        }

        if (this.editPublicChips()) {
            const yesCallback = this.saveFilterYesCallback.bind(this, name, dfd);
            const messageBox = new SavePublicFilterMessageBoxViewModel(yesCallback);
            RADApplication.getInstance().showMessageBox(messageBox);

            return promise;
        }

        this.saveFilterYesCallback(name, dfd);

        return promise;
    }

    private saveFilterYesCallback(filterName: string, deferred: JQueryDeferred<void>): void {
        this.isSaving = true;
        const filterExpressionObject = this.getCurrentFilterJson();

        this.persistFilterForName(filterName, filterExpressionObject);
        this.updateTooltips();

        this.activeChips([filterName]);
        this.filterViewModel.serializeActiveChips(this.activeChips());
        this.isSaving = false;
        deferred.resolve();
    }

    private getCurrentFilterJson(): FilterJson {
        const filterComponents = this.convertFilterElementsToFilterJson(this.filterViewModel.elements());
        const sortComponents = this.convertSortingElements(this.filterViewModel.sortElements());

        // remove last elements that should always be empty
        filterComponents.pop();
        sortComponents.pop();

        return {
            components: filterComponents,
            sorting: sortComponents
        } as FilterJson;
    }

    public persistFilterForName(filterName: string, filter: FilterJson): void {
        if (FilterChipsHelper.isCustom(filterName))
            return;

        if (FilterChipsHelper.isPublic(filterName)) {
            const filterDataForLv = this.publicUserData();
            filterDataForLv[FilterChipsHelper.removePublicPrefix(filterName)] = filter;

            this.setPublicData(filterDataForLv);
        }
        else {
            const key = this.buildKey();
            const filterDataForLv = this.getUserData(key)();
            filterDataForLv[FilterChipsHelper.removePrivatePrefix(filterName)] = filter;

            this.setUserData(key, filterDataForLv);
        }
    }

    private addFilterElementForFilterTerm(filterTerm: FilterTerm, connector: Connector = 'AND'): void {
        const column = filterTerm.column;
        const operator = filterTerm.operator;
        const value = filterTerm.value;

        this.addFilterRow(column, operator, value, connector);
    }

    private originValuesAsArray(): string[] {
        let values = this.originValues()
        if (TypeConverter.typeIsArray(typeof (values))) {
            return values as string[];
        }
        return (values as string).split(";").filter(title => title !== '');
    }

    private loadPersistedFilterExpressions(): void {
        let values = [];
        values = values.concat(this.loadCustomFilterChips(false));
        values = values.concat(this.loadPrivatePersistedFilterExpressions());
        values = values.concat(this.loadPublicPersistedFilterExpressions());
        values = values.concat(this.loadPreFilterExpression(values));
        this.values(values);
    }

    public addCustomFilterChips(customFilter: CustomFilterChip | Array<CustomFilterChip>): void {
        if (Array.isArray(customFilter))
            this.customFilterData = this.customFilterData.concat(customFilter);
        else
            this.customFilterData.push(customFilter);
    }

    private loadCustomFilterChips(isInit: boolean): string[] {
        if (this.customFilterData === null)
            return [];

        const customData = isInit ? this.customFilterData.filter(chip => chip.active) : this.customFilterData;
        const result = [];

        customData.forEach(data => result.push(FilterChipsHelper.addCustomPrefix(data.displayName.trim())))

        return result;
    }

    private loadPrivatePersistedFilterExpressions(): string[] {
        const key = this.buildKey();
        const existsUserSpecificDataForLv = this.currentUser.existUserSpecificData(key);

        if (!existsUserSpecificDataForLv) {
            return [];
        }
        const userSpecificData = this.getUserData(key);
        const validTitles = this.validateFilterUserData(userSpecificData());

        return validTitles.map(title => FilterChipsHelper.addPrivatePrefix(title.trim()));
    }

    private loadPublicPersistedFilterExpressions(): string[] {
        const existsPublicUserDataForLv = this.existPublicUserData();

        if (!existsPublicUserDataForLv) {
            return [];
        }

        const publicUserData = this.publicUserData;
        const validTitles = this.validateFilterUserData(publicUserData());

        return validTitles.map(title => FilterChipsHelper.addPublicPrefix(title.trim()));
    }

    /**
     * Validates a FilterJson
     * @param filterJson FilterJson stored for one Chip
     * @returns true for valid filter term, else false
     */
    private validatePersistedFilter(filterJson: FilterJson): boolean {
        if (Utilities.isNullOrUndefined(filterJson))
            return false;

        const availableColumns = this.filterViewModel.columns() as Array<{ field: string; filterable: boolean; hidden: boolean }>;

        const availableColumnNames = availableColumns.map(column => column.field);
        const filterableColumnNames = availableColumns.filter(column => column.filterable).map(column => column.field);

        const filterTerms = filterJson.components
            .filter(component => this.isCompleteFilterComponent(component.filter as FilterTerm))
            .filter(component => !_.isArray(component.filter))
            .map(component => component.filter as FilterTerm);

        const isEveryColumnAvailable = filterTerms.every(filterTerm => _.contains(availableColumnNames, filterTerm.column));
        const isEveryColumnFilterable = filterTerms.every(filterTerm => _.contains(filterableColumnNames, filterTerm.column));

        const isValid = isEveryColumnAvailable && isEveryColumnFilterable;

        return isValid;
    }

    private updateTooltips(): void {
        const key = this.buildKey();

        const userData = this.getUserData(key)();

        const privateTitles = Object.keys(userData);

        const tooltips = {} as Dictionary<string>;
        privateTitles
            .filter(chipTitle => chipTitle !== FilterChipsViewModel.getPreFilterLabel())
            .forEach((title) => tooltips[FilterChipsHelper.addPrivatePrefix(title)] = this.prettyPrintFilterExpression(userData[title]));

        const publicData = this.publicUserData();

        const publicTitles = Object.keys(publicData);

        publicTitles
            .forEach((title) => tooltips[FilterChipsHelper.addPublicPrefix(title)] = ResourceManager.getInstance().get("PUBLIC_FILTER_TOOLTIP") + ": " + this.prettyPrintFilterExpression(publicData[title]));

        if (!!privateTitles[FilterChipsViewModel.getPreFilterLabel()] && Utilities.isNotBlank(this.filterViewModel.staticFilter)) {
            tooltips[FilterChipsViewModel.getPreFilterLabel()] = this.filterViewModel.staticFilter;
        }

        this.tooltips(tooltips);
    }

    /**
     * Formatiert einen Filterausdruck (mehrere FilterTerme)
     * @param filterJson
     */
    private prettyPrintFilterExpression(filterJson: FilterJson): string {
        const components = filterJson.components;

        if (_.isEmpty(components)) {
            return '';
        }

        const pretties = components
            .filter(component => !!component.filter.column)
            .map(component => this.prettyPrintFilter(component.filter) + ' ' + ResourceManager.getInstance().get(component.connector));

        pretties[pretties.length - 1] = this.prettyPrintFilter(_.last(components).filter);

        return pretties.join('\r\n');
    }

    /**
     * Formatiert einen FilterTerm (Spalte + Operator + Wert)
     * @param filter
     */
    private prettyPrintFilter(filter: FilterTerm): string {
        let column = this.filterViewModel.columns().filter(c => c.field === filter.column)[0];

        if (Utilities.isNullOrUndefinedOrBlank(column))
            return '';

        const operator = this.operatorResourceLookup[filter.operator] || filter.operator || '';
        const value = this.prettyPrintValue(filter.value, column.typeName);

        return `${column.title} ${operator} ${value}`;
    }

    /**
     * Formatiert einen gefilterten Wert
     * @param value
     */
    private prettyPrintValue(value: Object, dotNetTypeName: string): string {
        const keys = _.keys(value);
        let prettyValue = '';

        // DateTime
        if (TypeConverter.typeIsDateTime(dotNetTypeName)) {
            return DateHelper.toString(DateHelper.parseDotNetDateTimeString(value[keys[0]] as string), false, true);
        }
        // Währungen
        else if (keys.length === 2 && keys[0].endsWith('ListViewValue.Value') && keys[1].endsWith('CurrencyID')) {
            const currencyValue = value[keys[0]];
            const currencyId = value[keys[1]];
            prettyValue = `${CurrencyHelper.formatCurrency(currencyValue, currencyId)}`;
        } else if (keys.length > 0) {
            prettyValue = value[keys[0]];
        }

        return prettyValue;
    }

    private convertFilterElementsToFilterJson(elements: Array<FilterElementViewModelT>): Array<FilterComponent> {
        const components = elements
            .map(element => this.convertFilterElementToFilterComponent(element));

        delete _.last(components).connector;

        return components;
    }

    private convertSortingElements(sortingElements: Array<FilterSortElementViewModelT>): Array<SortComponent> {
        return sortingElements.map(sortElement => this.convertFilterSortElementViewModel(sortElement));
    }

    private convertFilterElementToFilterComponent(element: FilterElementViewModelT): FilterComponent {
        const filterTerm = {} as FilterTerm;
        filterTerm.column = element.column();
        filterTerm.operator = element.operator();
        filterTerm.value = element.filterFactory().getValueForFilter();

        const filterComponent = {} as FilterComponent;
        filterComponent.connector = element.connector();
        filterComponent.filter = filterTerm;

        return filterComponent;
    }

    private convertFilterSortElementViewModel(sortElementViewModel: FilterSortElementViewModelT): SortComponent {
        return {
            sortColumn: sortElementViewModel.sortColumnList.value(),
            sortDirection: sortElementViewModel.sortDirectionLink.direction()
        };
    }

    public loadNamedFilter(name: string): FilterJson {
        const key = this.buildKey();
        if (FilterChipsHelper.isPublic(name)) {
            return this.publicUserData()[FilterChipsHelper.removePublicPrefix(name)]
        }
        else if (FilterChipsHelper.isPrivate(name)) {
            return this.getUserData(key)()[FilterChipsHelper.removePrivatePrefix(name)]
        }

        return null;
    }

    public static getPreFilterLabel(): string {
        return ResourceManager.getInstance().get(PREFILTER);
    }

    private buildKey(): string {
        return `LV: ${this.pdTypeName}`;
    }

    private existsPrivateFilterData(name: string): boolean {
        const key = this.buildKey();
        const userData = this.getUserData(key);

        return !!userData()[name];
    }

    private existsPublicFilterData(name: string): boolean {
        const key = this.buildKey();
        const publicData = this.publicUserData;

        return !!publicData()[name];
    }

    private validateFilterUserData(userData: Dictionary<FilterJson>): Array<String> {
        const filterTitles = Object.keys(userData);

        const validTitles = filterTitles.filter(title => this.validatePersistedFilter(userData[title]));

        return validTitles;
    }

    private getUserData(key: string): KnockoutObservable<Dictionary<FilterJson>> {
        const defaultValue = {};
        return this.currentUser.getUserSpecificData(key, defaultValue);
    }

    private setUserData(key: string, data: Dictionary<FilterJson>): void {
        this.currentUser.setUserSpecificData(key, data, false);
    }

    private setPublicData(data: Dictionary<FilterJson>): void {
        if (data == null) {
            this.publicUserData({});
            return;
        }
        this.publicUserData(data);
    }

    public updatePublicUserData(onlyLoad: boolean = false): JQueryPromise<void> {
        return RADEnvironmentProxy.getInstance().getPublicUserData({ key: this.buildKey() }).then((publicUserData) => {
            if (publicUserData != null) {
                try {
                    const value = JSON.parse(publicUserData);

                    const oldValue = this.preventPublicUserDataServerUpdate;
                    try {
                        // Unschön, aber wir wollen nicht, dass bei onlyLoad = true ein Update zum Server gesendet wird
                        this.preventPublicUserDataServerUpdate = onlyLoad;
                        this.publicUserData(value);
                    }
                    finally {
                        this.preventPublicUserDataServerUpdate = oldValue;
                    }
                } catch (e) {
                    //if there is no valid public Data for the given List leave Dictionary empty
                }
            }
        }).promise();
    }

    /**
     * Prüft ob für ein key PublicUserData vorhanden sind
     */
    private existPublicUserData(): boolean {
        return this.publicUserData != null && this.publicUserData() != null && Object.keys(this.publicUserData()).length > 0;
    }

    private addFilterRow(column: string, operator: string, value: object, connector: 'AND' | 'OR',) {
        const filterParams = {
            connector: connector,
            operator: operator,
            column: column,
            value: value
        };

        const index = undefined;
        this.filterViewModel.addNewElementWithParameters(filterParams, index, false);
    }

    private addSortRow(sortColumn: any = null, sortDirection: 'ASC' | 'DESC' = 'ASC', index: number): void {
        const sortElementParams = {
            sortColumn: sortColumn,
            sortDirection: sortDirection.toUpperCase(),
            filterViewModel: this.filterViewModel
        };

        this.filterViewModel.addNewSortElementWithParams(sortElementParams, index, false);
    }

    /**
     * @override
     */
    public onRemove(chipTitle: string, showMessageBox: boolean = true, sendMessage: boolean = true): void {
        if (FilterChipsHelper.isPublic(chipTitle) && !RADApplication.getInstance().user.isAllowedConfigurePublicUserData())
            return;

        if (!showMessageBox) {
            super.onRemove(chipTitle);
            return;
        }

        const yesCallback = this.deleteChipYesCallback.bind(this, chipTitle, (value) => {
            super.onRemove(value);
            if(sendMessage)
                Messenger.send(this.createChipsChangedMessage('REMOVE', value));
        });

        RADApplication.getInstance().showMessageBox(new DeleteFilterMessageBoxViewModel(yesCallback));
    }

    /**
     * @override
     */
    public onAdd(value: string, sendMessage: boolean = true): void {
        super.onAdd(value);

        if (sendMessage)
            Messenger.send(this.createChipsChangedMessage('ADD', value));
    }

    /**
     * @override
     */
    public getValueFromID(id: string): string {
        if (FilterChipsHelper.isPrivate(id))
            return FilterChipsHelper.removePrivatePrefix(id);

        if (FilterChipsHelper.isPublic(id))
            return FilterChipsHelper.removePublicPrefix(id);

        if (FilterChipsHelper.isCustom(id))
            return FilterChipsHelper.removeCustomPrefix(id);

        return super.getValueFromID(id);
    }

    /**
     * @override
     */
    public showRemove(id: string): boolean {
        return super.showRemove(id) && (FilterChipsHelper.isPrivate(id) || (this.editPublicChips() && FilterChipsHelper.isPublic(id)));
    }

    /**
     * @override
     */
    protected getTypeSafeValue<T = string>(value: string): T {
        return this.editPublicChips() ? FilterChipsHelper.addPublicPrefix(value) as any : FilterChipsHelper.addPrivatePrefix(value) as any;
    }

    /**
     * @override
     */
    protected readValues(observable: KnockoutObservable<string | Array<string>>): string[] {
        return this.readValuesInternal(observable, this.editPrivateChips(), this.editPublicChips());
    }

    /**
     * @override
     */
    protected readAllValues(observable: KnockoutObservable<string | Array<string>>): string[] {
        return this.readValuesInternal(observable, false, false);
    }

    private readValuesInternal(observable: KnockoutObservable<string | Array<string>>, readPrivateOnly: boolean, readPublicOnly: boolean): string[] {
        let value = observable();

        if (Array.isArray(value))
            return [...value]; // Kopie des Arrays herausgeben

        if (!this.sortAlphabetically())
            return FilterChipsHelper.getValues(value, readPublicOnly, readPrivateOnly);
        else
            return FilterChipsHelper.getValuesAlphabetically(value, readPublicOnly, readPrivateOnly);
    }

    private deleteChipYesCallback(chipTitle: string, callback: Function): void {
        const key = this.buildKey();
        if (FilterChipsHelper.isPublic(chipTitle)) {
            const publicData = this.publicUserData();

            delete publicData[FilterChipsHelper.removePublicPrefix(chipTitle)];
            this.setPublicData(publicData);
            // die Änderung der öffentlichen Filter muss erst an den Server kommuniziert sein, bevor im Callback der Filter als geändert markiert wird
            // ansonsten kann vorkommen, dass in verbundenen ALVs die öffentlichen Filter neu geladen werden, bevor die Änderungen in Kraft treten
            this.waitForPublicData.then(() =>
            {
                callback(chipTitle);
            });
        }
        else {
            const userData = this.getUserData(key)();
            delete userData[FilterChipsHelper.removePrivatePrefix(chipTitle)];
            this.setUserData(key, userData);
            callback(chipTitle);
        }

        if (_.contains(this.activeChips(), chipTitle)) {
            this.activeChips(this.activeChips().filter(anyChipTitle => anyChipTitle !== chipTitle));
        }
    }

    private createChipsChangedMessage(action: string, value: string): FilterChipsChangedMessage {
        return new FilterChipsChangedMessage({
            action: action,
            listViewPdoId: this.listViewPdoId,
            chipName: value,
            isPublicChip: FilterChipsHelper.isPublic(value),
            chipsControlId: this.identifier
        })
    }

    protected disposeInternal(): void {
        Messenger.unregisterForMessageId('FilterChipsChanged', null, this);
        super.disposeInternal();
    }
}

ComponentManager.setViewModel('FilterChips', FilterChipsViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');